import type { AnalyticsParamValue } from '@sunflower-games/webview-bridge';

import { logToNewRelic } from '../debug';

import { hasSent, markSent } from './conversionDedupe';
import { narrowParams } from './narrowParams';
import { getShellBridge } from './shellBridge';
import { confirmShell } from './shellPresence';

// AppsFlyer conversions over the shell's `analytics.event`, with the old Android app's names and `af_` params minus its PII.

// The shell's `analytics.event` bounds; it drops the whole event when one entry breaks them.
const PARAM_KEY = /^[a-z0-9_]{1,40}$/;
const MAX_PARAMS = 20;
const STRING_MAX_LENGTH = 200;

const toShellValue = (value: unknown): AnalyticsParamValue | undefined =>
  typeof value === 'boolean' ||
  (typeof value === 'number' && Number.isFinite(value)) ||
  (typeof value === 'string' && value.length >= 1 && value.length <= STRING_MAX_LENGTH)
    ? value
    : undefined;

/** Drops entries the shell would refuse instead of losing the event; earlier keys win the cap. */
export function toShellAnalyticsParams(params: Record<string, unknown>): Record<string, AnalyticsParamValue> {
  return narrowParams(params, (key) => PARAM_KEY.test(key), toShellValue, MAX_PARAMS);
}

/** @returns Whether the event left for the shell. */
async function sendToShell(name: string, params: Record<string, unknown>): Promise<boolean> {
  if ((await confirmShell()) !== 'confirmed') return false;
  const bridge = getShellBridge();
  if (bridge === null) return false;
  try {
    bridge.send('analytics.event', { name, params: toShellAnalyticsParams(params) });
    return true;
  } catch (error) {
    logToNewRelic('Shell AppsFlyer event dropped', { name, error: String(error) }, 'warn');
    return false;
  }
}

const inFlight = new Set<string>();

// Marked only once the event has left, so a dropped send can still report later.
async function sendOnce(key: string, name: string, params: Record<string, unknown>): Promise<void> {
  if (inFlight.has(key) || hasSent(key)) return;
  inFlight.add(key);
  try {
    if (await sendToShell(name, params)) markSent(key);
  } finally {
    inFlight.delete(key);
  }
}

let identifiedUserId: string | null | undefined;

/** @returns Whether the identity left for the shell. */
function sendIdentity(userId: string | null, presence: string): boolean {
  if (presence !== 'confirmed') return false;
  const bridge = getShellBridge();
  if (bridge === null) return false;
  try {
    if (userId === null) {
      bridge.send('user.logout', {});
    } else {
      bridge.send('user.identify', { userId });
    }
    return true;
  } catch (error) {
    logToNewRelic('Shell identity not sent', { error: String(error) }, 'warn');
    return false;
  }
}

/** Sets (`null` clears) the shell's user id for AppsFlyer, native Firebase and push, regardless of `optimove_enabled`. */
export function syncShellIdentity(userId: string | null): void {
  if (userId === identifiedUserId) return;
  identifiedUserId = userId;
  void confirmShell().then((presence) => {
    // Released on a drop, so the next call with this id retries; a duplicate identify is harmless, a lost one is not.
    if (!sendIdentity(userId, presence) && identifiedUserId === userId) identifiedUserId = undefined;
  });
}

export function trackShellSignUpClick(provider: string): void {
  void sendToShell('sign_up_click', { af_provider: provider });
}

/** Once per account: `af_user_id` takes the slot the old app gave `af_email`. */
export function trackShellSignUpVerified({ userId, method }: { userId: string; method: string }): void {
  // Identify first: on social sign-up the auth reaction only identifies after the welcome dialog.
  syncShellIdentity(userId);
  void sendOnce(`af:signup:${userId}`, 'sign_up_verified', {
    af_complete_registration: 'true',
    af_registration_method: method,
    af_user_id: userId,
  });
}

/** Once per account when the uid is known. */
export function trackShellPhoneVerified(userId: string | undefined): void {
  if (userId === undefined) {
    void sendToShell('phone_verified', {});
    return;
  }
  void sendOnce(`af:phone:${userId}`, 'phone_verified', { af_user_id: userId });
}

/** `purchase_event`, plus `ftd` on a first deposit, each once per transaction id. */
export function trackShellPurchase({
  userId,
  transactionId,
  amount,
  currency,
  signUpDate,
  ftd,
}: {
  userId: string | undefined;
  transactionId: string;
  amount: number;
  currency: string;
  signUpDate: string;
  ftd: boolean;
}): void {
  const params = {
    af_user_id: userId,
    af_transaction_id: transactionId,
    af_currency: currency,
    af_revenue: amount,
    af_price: amount,
    af_value: amount,
    af_ftd: ftd,
    af_sign_up_date: signUpDate,
  };
  void sendOnce(`af:purchase:${transactionId}`, 'purchase_event', params);
  if (ftd) void sendOnce(`af:ftd:${transactionId}`, 'ftd', params);
}

/** Test seam: forget the identified user and in-flight conversions. */
export function resetShellAppsFlyer(): void {
  identifiedUserId = undefined;
  inFlight.clear();
}
